// src/components/app-components/find-investors-modules/InvestorProfileApp.jsx
import { useState, useRef, useEffect, useCallback } from 'react';
import { API_URL } from '../../../config';
import { Link, useParams } from 'react-router-dom';
import InvestorLogo from '../../../components/data/InvestorLogo';
import LoadingSpinner from './../../../components/ui/LoadingSpinner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import CountryFlag from '../../../components/countryFlags/countryFlag';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { UserPlus, Loader2, CheckCircle2 } from 'lucide-react';
import InvestorDealsCount from './investorDeals';
import InvestorDealsChart from './investorDealsChart';
import InvestorLastInvestments from './investorLastInvestments';
import CoInvestNetworkApp from './co-investment-network/CoInvestNetworkApp';
import FindInvestorsHeader from '../find-investors-modules/header';
import removeInvestorIcon from '../icons/remove-inv-from-dashboard.svg';
import InvestorAccessIcons from '../../../components/investor-display/InvestorAccessIcons';
import InvestorContactDetails from '../../../components/investor-display/InvestorContactDetails';
import { useMembershipAccess } from '../../../hooks/membership';
import { useCRM } from '../../../hooks/crm';

const InvestorProfileApp = () => {
  const { investorId } = useParams();
  const [investorData, setInvestorData] = useState(null);
  const [loadingInvestor, setLoadingInvestor] = useState(false);
  const { t, i18n } = useTranslation();
  const languagePrefix = i18n.language === 'en' ? '' : `/${i18n.language}`;
  const {
    canAddInvestor,
    canSeeSensitiveInvestorInfo: canSeeSensitiveInfo,
  } = useMembershipAccess();
  const {
    selectedBoard,
    addInvestorToBoard,
    removeInvestorFromBoard,
    getInvestorColumnId,
  } = useCRM();

  const itemsPerPage = 5;

  // Chart (aggregated from /stats)
  const [chartData, setChartData] = useState([]);

  // Counters (from /stats)
  const [leadOnlyCount, setLeadOnlyCount] = useState(0);
  const [secondOnlyCount, setSecondOnlyCount] = useState(0);
  const [bothCount, setBothCount] = useState(0);
  const [totalDealsForCounter, setTotalDealsForCounter] = useState(0);

  // For the deals list (as in the public schema)
  const [visibleDeals, setVisibleDeals] = useState([]);
  const [dealsToShow, setDealsToShow] = useState(null); // null means not known yet
  const [totalDealsCount, setTotalDealsCount] = useState(0);
  const [hiddenMeta, setHiddenMeta] = useState({ page: 1, perPage: itemsPerPage, pages: 1, total: 0 });
  const hiddenPagesCacheRef = useRef(new Map());
  const visiblePagesCacheRef = useRef(new Map()); // NEW: cache of visible pages
  const [visibleMeta, setVisibleMeta] = useState({ page: 1, perPage: itemsPerPage, pages: 1, total: 0 });
  const [listReady, setListReady] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  // Enrich deal cards with lead/participated flags
  const enrichWithFlags = (arr, inv) => {
    const leadSet = new Set((inv?.as_lead_investor || []).map(String));
    const partSet = new Set((inv?.as_investor_participated || []).map(String));
    return (arr || []).map(d => ({
      ...d,
      lead: leadSet.has(String(d._id)),
      participated: partSet.has(String(d._id)),
    }));
  };

  // 1) Fetch the investor (membership-aware + has from summary)
  useEffect(() => {
    const investorIdFromUrl = investorId.split('-')[0];

    const fetchInvestorData = async () => {
      try {
        const identifier = String(investorIdFromUrl).split('-')[0].trim();
        const url = `${API_URL}/investors/${identifier}`;
        const response = await axios.get(url, {
          params: { by: 'id' },
          withCredentials: true
        });

        const data = response.data || null;

        if (data) {
          data.as_lead_investor = Array.isArray(data.as_lead_investor) ? data.as_lead_investor : [];
          data.as_investor_participated = Array.isArray(data.as_investor_participated) ? data.as_investor_participated : [];
          setInvestorData(data);
        } else {
          console.error('Investor not found');
        }
      } catch (error) {
        console.error('Error fetching investor:', error);
      }
    };

    fetchInvestorData();
    window.scrollTo(0, 0);
  }, [investorId]);

  useEffect(() => {
    if (window.location.hash === '#last-investments') {
      const el = document.getElementById('last-investments');
      if (el) {
        // Wait a bit to ensure the DOM has fully loaded
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200); // can be 0–300 ms, depending on load
      }
    }
  }, [listReady]);

  // 2) Fetch /stats and the first batch of /last-deals (quickly, from summary)
  useEffect(() => {
    const run = async () => {
      if (!investorData) {
        setVisibleDeals([]);
        setDealsToShow(null);
        setTotalDealsCount(0);
        setHiddenMeta({ page: 1, perPage: itemsPerPage, pages: 1, total: 0 });
        hiddenPagesCacheRef.current.clear();
        visiblePagesCacheRef.current.clear();
        setVisibleMeta({ page: 1, perPage: itemsPerPage, pages: 1, total: 0 });
        setChartData([]);
        setListVersion(v => v + 1);
        setListReady(false);
        return;
      }

      // 2.1 stats
      try {
        const statsUrl = `${API_URL}/investors/${investorData.id}/stats`;
        const { data: st } = await axios.get(
          statsUrl,
          { params: { by: 'id' }, withCredentials: true }
        );

        // counters
        setLeadOnlyCount(Number(st?.lead_only || 0));
        setSecondOnlyCount(Number(st?.second_only || 0));
        setBothCount(Number(st?.both || 0));
        setTotalDealsForCounter(Number(st?.deals_total || 0));
        setTotalDealsCount(Number(st?.deals_total || 0));

        // chart
        const years = Array.isArray(st?.years) ? st.years : [];
        const chart = years.map(r => ({
          year: String(r.y),
          allDeals: Number(r.all || 0),
          leadInvestor: Number(r.lead || 0),
          secondInvestor: Number(r.second || 0),
        }));
        setChartData(chart);
      } catch (e) {
        console.error('stats error', e);
        setChartData([]);
      }

      // 2.2 last-deals (first batch + hidden meta): USE THE AUTHORIZED ROUTE
      try {
        console.log('[investorProfileApp] fetching deals for investor:', {
          investorId: investorData.id,
          investorName: investorData.name,
          investorOid: investorData._id
        });

        // Authorized route returns membership-aware visible/hidden data + visible_meta
        const dealsUrl = `${API_URL}/investors/${investorData.id}/last-deals`;
        const resp = await axios.get(dealsUrl, {
          params: {
            by: 'id',
            // hidden
            page: 1,
            perPage: itemsPerPage,
            // visible
            visiblePage: 1,
            visiblePerPage: itemsPerPage,
          },
          withCredentials: true
        });

        const payload = resp.data || {};
        console.log('[investorProfileApp] API response:', {
          total_deals_count: payload.total_deals_count,
          deals_to_show: payload.deals_to_show,
          visible_deals_count: payload.visible_deals?.length || 0,
          hidden_deals_count: payload.hidden_deals?.items?.length || 0
        });

        const h = payload.hidden_deals || {};

        const visibleFirst = enrichWithFlags(payload.visible_deals || [], investorData);
        const hiddenFirst = enrichWithFlags(h.items || [], investorData);

        // visible: store the CURRENT page + metadata from visible_meta
        setVisibleDeals(visibleFirst);
        setDealsToShow(Number(payload.deals_to_show || 0)); // for pro = total
        setTotalDealsCount(Number(payload.total_deals_count || 0));

        const vm = payload.visible_meta || {};
        setVisibleMeta({
          page: vm.page || 1,
          perPage: vm.perPage || itemsPerPage,
          pages: vm.pages || 1,
          total: vm.total ?? (payload.deals_to_show || 0),
        });
        visiblePagesCacheRef.current.clear();
        if (visibleFirst.length) {
          visiblePagesCacheRef.current.set(vm.page || 1, visibleFirst);
        }

        // hidden: as before
        setHiddenMeta({
          page: h.page || 1,
          perPage: h.perPage || itemsPerPage,
          pages: h.pages || 1,
          total: h.total || 0,
        });
        hiddenPagesCacheRef.current.clear();
        if ((h.items || []).length) hiddenPagesCacheRef.current.set(h.page, hiddenFirst);

        setListVersion(v => v + 1);
        setListReady(true);
      } catch (e) {
        console.error('last-deals error', e);
        setVisibleDeals([]);
        setDealsToShow(0);
        setHiddenMeta({ page: 1, perPage: itemsPerPage, pages: 1, total: 0 });
        setListVersion(v => v + 1);
        setListReady(true);
      }
    };

    run();
  }, [investorData, itemsPerPage]);

  // 3) Unified pagination: full visible items + redacted hidden items as requested
  const getUnifiedPage = async (page) => {
    if (dealsToShow === null) {
      return { items: [], totalPages: 1, currentPage: 1 };
    }

    const PAGE_SIZE = itemsPerPage;
    const total = totalDealsCount;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(Math.max(1, page), totalPages);

    const startIdx = (safePage - 1) * PAGE_SIZE;
    const endIdx = Math.min(startIdx + PAGE_SIZE, total);

    const items = [];

    for (let gi = startIdx; gi < endIdx; gi++) {
      // --- VISIBLE AREA (full), index < dealsToShow ---
      if (gi < dealsToShow) {
        const offsetInVisible = gi; // 0..dealsToShow-1
        const vPage = Math.floor(offsetInVisible / visibleMeta.perPage) + 1;
        const vOffset = offsetInVisible % visibleMeta.perPage;

        let vItems = visiblePagesCacheRef.current.get(vPage);
        if (!vItems) {
          // fetch the needed visible page
          const dealsUrl = `${API_URL}/investors/${investorData.id}/last-deals`;
          const { data } = await axios.get(dealsUrl, {
            params: {
              by: 'id',
              // hidden items can stay current: the server returns them quickly and in small numbers
              page: hiddenMeta.page,
              perPage: hiddenMeta.perPage,
              // visible: the page we actually need
              visiblePage: vPage,
              visiblePerPage: visibleMeta.perPage,
            },
            withCredentials: true
          });

          const vmeta = data.visible_meta || {};
          const enriched = enrichWithFlags(data.visible_deals || [], investorData);
          visiblePagesCacheRef.current.set(vmeta.page || vPage, enriched);

          setVisibleMeta(prev => ({
            page: vmeta.page ?? prev.page,
            perPage: vmeta.perPage ?? prev.perPage,
            pages: vmeta.pages ?? prev.pages,
            total: vmeta.total ?? prev.total,
          }));

          vItems = enriched;
        }

        const fullItem = vItems[vOffset];
        if (fullItem) items.push({ ...fullItem, redacted: false });
        continue;
      }

      // --- HIDDEN AREA (redacted), index >= dealsToShow ---
      const h = gi - dealsToShow;
      if (h < 0) continue;

      const serverPage = Math.floor(h / hiddenMeta.perPage) + 1;
      const offsetInServerPage = h % hiddenMeta.perPage;

      let serverPageItems = hiddenPagesCacheRef.current.get(serverPage);
      if (!serverPageItems) {
        const dealsUrl = `${API_URL}/investors-limited/${investorData.id}/last-deals`;
        const { data } = await axios.get(dealsUrl, {
          params: {
            by: 'id',
            page: serverPage,
            perPage: hiddenMeta.perPage,
          },
          withCredentials: true
        });

        const hblock = data.hidden_deals || {};
        const enriched = enrichWithFlags(hblock.items || [], investorData);
        hiddenPagesCacheRef.current.set(hblock.page || serverPage, enriched);

        setHiddenMeta(prev => ({
          page: hblock.page ?? prev.page,
          perPage: hblock.perPage ?? prev.perPage,
          pages: hblock.pages ?? prev.pages,
          total: hblock.total ?? prev.total,
        }));

        serverPageItems = enriched;
      }

      const red = serverPageItems[offsetInServerPage];
      if (red) items.push({ ...red, redacted: true });
    }

    return { items, totalPages, currentPage: safePage };
  };

  const isInvestorInBoard = (() => {
    if (!selectedBoard || !investorData || !selectedBoard.columns) return false;
    return selectedBoard.columns.some(col =>
      col.investors.some(inv => inv.investor_id.toString() === investorData._id.toString())
    );
  })();

  const handleAddInvestor = async () => {
    if (!selectedBoard || !canAddInvestor) return;
    setLoadingInvestor(true);
    try {
      await addInvestorToBoard({
        boardId: selectedBoard.board_id,
        columnId: 1,
        investor: investorData,
      });
    } catch (error) {
      console.error('Error adding investor to board:', error);
    } finally {
      setLoadingInvestor(false);
    }
  };

  const handleRemoveInvestor = async () => {
    if (!selectedBoard || !isInvestorInBoard) return;
    setLoadingInvestor(true);
    try {
      const column = getInvestorColumnId(investorData._id, selectedBoard.board_id);

      if (!column) {
        console.error('Investor not found in any column');
        return;
      }

      await removeInvestorFromBoard({
        boardId: selectedBoard.board_id,
        columnId: column,
        investorId: investorData._id,
      });
    } catch (error) {
      console.error('Error removing investor from board:', error);
    } finally {
      setLoadingInvestor(false);
    }
  };

  if (!investorData) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container mx-auto px-4">
      {/* Header Section */}
      <div className="w-full mb-6">
        <div className="mb-4">
          <Link
            to={`${languagePrefix}/app/find-investors`}
            className="cursor-pointer text-secondary hover:underline"
          >
            ← {t('pageInvProf.backtoBtn')}
          </Link>
        </div>
        <div className="flex flex-col md:flex-row items-center justify-between w-full">
          {/* Left Side: Investor Name */}
          <div className="flex-1">
            <h2 className="flex items-center text-2xl font-semibold break-words">
              <InvestorLogo
                id={investorData.id}
                country={investorData.country}
                className="w-6 h-6"
                alt={`${investorData.name} logo`}
              />
              <span className="ml-2">{investorData.name}</span>
            </h2>
          </div>

          {/* Right Side: Functionalities: styles and behavior as in InvestorCard */}
          <div className="flex flex-col md:flex-row items-center md:space-x-4 mt-4 md:mt-0">
            <div className="relative group">
              {loadingInvestor ? (
                <Button
                  variant="default"
                  size="xs"
                  wrap="nowrap"
                  disabled
                  iconLeft={<Loader2 className="animate-spin" />}
                  className="min-w-[120px] !bg-stone-100 !text-stone-950 !cursor-default"
                  aria-busy="true"
                >
                  adding…
                </Button>
              ) : isInvestorInBoard ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="default"
                    size="xs"
                    wrap="nowrap"
                    disabled
                    iconLeft={<CheckCircle2 />}
                    className="min-w-[120px] !bg-stone-100 !text-stone-950 !cursor-default"
                    title="Already in your CRM"
                  >
                    added
                  </Button>
                  <button
                    onClick={handleRemoveInvestor}
                    className="cursor-pointer text-stone-950 hover:text-red-700"
                    aria-label="Remove from CRM"
                    title="Remove from CRM"
                  >
                    <img src={removeInvestorIcon} alt="Remove" className="w-4 h-4" loading="lazy" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={handleAddInvestor}
                  variant="default"
                  size="xs"
                  wrap="nowrap"
                  iconLeft={<UserPlus />}
                  className="min-w-[120px] !bg-stone-100 !text-stone-950"
                  disabled={!canAddInvestor}
                  aria-disabled={!canAddInvestor}
                  title={canAddInvestor ? 'Add to CRM' : 'Requires Startup/Professional'}
                >
                  to CRM
                </Button>
              )}

              {/* tooltip as in the first component: only when there is no access */}
              {!canAddInvestor && (
                <div className="tooltip-content absolute top-1/2 -translate-y-1/2 left-full ml-2 px-3 py-2 bg-stone-700 text-xs text-white rounded-lg shadow-lg hidden group-hover:block z-10">
                  {t('pageFindInvestors.requires1')}
                </div>
              )}
            </div>

            {/* Board Selection Dropdown */}
            <div className="mt-4 md:mt-0">
              <FindInvestorsHeader allowWrap={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Investor Information Block */}
      <div className="w-full mb-6 bg-gradient-to-r from-slate-50 to-gray-100 p-6 rounded-md shadow-md">
        {investorData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg">
              {/* Left Block */}
              <div className="flex flex-col gap-4 bg-white p-4 rounded-lg shadow">
                <div className="mt-1">
                  <InvestorAccessIcons
                    investor={investorData}
                    socialMode={canSeeSensitiveInfo ? 'direct' : 'hint'}
                    showContactIcons={!canSeeSensitiveInfo}
                    className="flex items-center flex-wrap gap-3"
                  />
                </div>

                {canSeeSensitiveInfo && (
                  <InvestorContactDetails
                    investor={investorData}
                    className="text-sm text-gray-700"
                  />
                )}

                {investorData.address && (
                  <div className="flex flex-col">
                    <strong className="text-sm">{t('pageFindInvestors.invAddress')}:</strong>
                    <span className="text-sm">{investorData.address}</span>
                  </div>
                )}
                {investorData.country && (
                  <div className="flex flex-col">
                    <strong className="text-sm">{t('pageFindInvestors.invCountry')}:</strong>
                    <CountryFlag countryName={investorData.country} />
                  </div>
                )}
              </div>

              {/* Right Block */}
              {investorData.industries && investorData.industries.length > 0 && (
                <div className="bg-white p-4 rounded-lg shadow">
                  <strong className="text-sm">{t('pageFindInvestors.invIndustry')}:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {investorData.industries.map((industry, index) => (
                      <Badge
                        key={index}
                        variant="v1"
                        text={industry.trim()}
                        as="a"
                        href={`${languagePrefix}/app/find-investors?industries=${encodeURIComponent(industry.trim())}`}
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Deals Count */}
            <div className="mt-4">
              <InvestorDealsCount
                totalDeals={totalDealsForCounter}
                leadCount={leadOnlyCount}
                participatedCount={secondOnlyCount}
                bothCount={bothCount}
              />
            </div>
          </>
        )}
      </div>

      {/* Main Content Area */}
      <div className="w-full">
        <div className="rounded-md mb-6 bg-white p-4 shadow">
          <InvestorDealsChart data={chartData} />
        </div>

        {/* Co-Investment Network */}
        <div className="mb-6">
          <CoInvestNetworkApp investorId={investorData.id} />
        </div>

        <div id="last-investments" className="rounded-md bg-white p-6 shadow">
          <InvestorLastInvestments
            totalDeals={totalDealsCount}
            visibleCount={dealsToShow}
            getUnifiedPage={getUnifiedPage}
            listVersion={listVersion}
            initialized={listReady && dealsToShow !== null}
          />
        </div>
      </div>
    </div>
  );
};

export default InvestorProfileApp;
